import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { checkUsernameAvailability } from '@/lib/api/auth/api';

interface UseUsernameAvailabilityResult {
    isChecking: boolean;
    isAvailable: boolean | null;
    error: string | null;
}

export function useUsernameAvailability(username: string): UseUsernameAvailabilityResult {
    const [isChecking, setIsChecking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Debounce username input to avoid excessive API calls (500ms delay)
    const debouncedUsername = useDebounce(username, 500);

    useEffect(() => {
        // Reset state if username is too short or empty
        if (!debouncedUsername || debouncedUsername.length < 5) {
            setIsAvailable(null);
            setError(null);
            setIsChecking(false);
            return;
        }

        // Check username availability
        const checkAvailability = async () => {
            setIsChecking(true);
            setError(null);

            try {
                const response = await checkUsernameAvailability(debouncedUsername);
                // Backend returns { username: boolean } where true = taken, false = available
                setIsAvailable(!response.username);
            } catch {
                setError('Unable to verify username availability. Please contact friends@stanwith.me for assistance.');
                setIsAvailable(null);
            } finally {
                setIsChecking(false);
            }
        };

        checkAvailability();
    }, [debouncedUsername]);

    return {
        isChecking,
        isAvailable,
        error,
    };
}
